import { Component, computed, OnInit, Signal } from '@angular/core';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { AuthService } from '@services/auth/auth.service';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { Router, RouterLink } from '@angular/router';
import { ToastService } from '@services/helper/toast.service';
import { HelperService } from '@services/helper/helper.service';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { AuthenticatedUser, DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { LoadingComponent } from '@components/loading/loading.component';
import { AdminService } from '@services/admin/admin.service';
import { ControlledDashboardOwnerUser } from '@airwallet/shared-models/controlled-dashboard-owner-user';

@Component({
    selector: 'app-admin-sidebar',
    imports: [FormsModule, TranslateModule, RouterLink, LoadingComponent],
    templateUrl: './admin-sidebar.component.html',
    styleUrl: './admin-sidebar.component.scss'
})
export class AdminSidebarComponent implements OnInit {
    isAdmin: Signal<boolean> = this.authService.isAdmin;

    controlledUid: string;
    dashboardOwnerUser: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();
    controlledUser: ControlledDashboardOwnerUser = this.localStorageService.getItem('controlled_user');
    authenticatedUser: AuthenticatedUser = this.localStorageService.getItem('authenticatedUser');
    mfaEnrollments: string;

    operatorUID: Signal<string> = computed(() => {
        return this.controlledUser.uid.split('_operated_by_')[1] || 'N/A';
    });
    regionCountry: Signal<string> = computed(() => {
        if (!this.controlledUser?.billing?.shallow_account_details?.address) {
            return 'N/A';
        }
        const region: string = 'Region ' + this.controlledUser.settings.stripe_region?.toUpperCase();
        const country: string = this.helperService.getCountryTranslated(this.controlledUser.billing.shallow_account_details.address.country, true);
        return region + ' / ' + country;
    });

    constructor(
        protected sidebarService: SidebarService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private toastService: ToastService,
        private translate: TranslateService,
        protected helperService: HelperService,
        private featureService: FeatureService,
        protected adminService: AdminService
    ) {}

    ngOnInit() {
        this.handleControlActive();
        this.authService.permissionsReady.then(() => {
            if (this.isAdmin()) {
                this.sidebarService.enableAdminSidebar();
            }
        });
        if (this.controlledUser && this.controlledUser.enrolledFactors) {
            for (const enrollment of this.controlledUser.enrolledFactors) {
                this.mfaEnrollments = this.mfaEnrollments ? this.mfaEnrollments + ', ' + enrollment.displayName : enrollment.displayName;
            }
        }
    }

    handleControlActive() {
        if (this.localStorageService.getItem('controlled_user') !== null) {
            this.sidebarService.enableAdminSidebar();
            this.adminService.controlActive.set(true);
            this.adminService.controlledUid.set(this.dashboardOwnerUser.uid);
            this.controlledUid = this.dashboardOwnerUser.uid;
        } else {
            this.adminService.controlActive.set(false);
        }
    }

    async exitControlledState() {
        if (!this.adminService.controlActive()) {
            this.toastService.warning("Bro.. You're not even controlling any user");
            return;
        }
        this.adminService.loadingControlledUid.set(true);
        this.adminService.controlledUid.set('');
        this.resetLocalStorage();
        await this.router.navigateByUrl('/home', { skipLocationChange: false }).then(async result => {
            this.router.navigate([`/home`]).then(res => {
                setTimeout(() => {
                    this.adminService.loadingControlledUid.set(false);
                }, 500);
            });
        });
        this.adminService.controlActive.set(false);
        window.location.reload();
    }

    private resetLocalStorage() {
        this.localStorageService.removeItem('controlled_user');
        this.localStorageService.removeItem('owner');
        this.localStorageService.setItem('authenticatedUser', this.localStorageService.getItem('admin_AuthenticatedUser'));
        this.localStorageService.setItem('dashboardOwnerUser', this.localStorageService.getItem('admin_DashboardOwnerUser'));
        this.localStorageService.setItem('featureAccess', this.localStorageService.getItem('admin_FeatureAccess'));
        this.localStorageService.setItem('featureAccessChecksum', this.localStorageService.getItem('admin_FeatureAccessChecksum'));
        this.localStorageService.setItem('subscriptionPlanType', this.localStorageService.getItem('admin_SubscriptionPlanType'));
        this.localStorageService.removeItem('admin_AuthenticatedUser');
        this.localStorageService.removeItem('admin_FeatureAccess');
        this.localStorageService.removeItem('admin_FeatureAccessChecksum');
        this.localStorageService.removeItem('admin_SubscriptionPlanType');
        this.localStorageService.removeItem('samlMembershipOwners');
        this.localStorageService.removeItem('returnUrl');
    }

    async enterControlledState(controlledUid: string) {
        this.adminService.loadingControlledUid.set(true);

        if (!controlledUid) {
            this.toastService.info(this.translate.instant('navbar.customer_not_found'));
            this.adminService.loadingControlledUid.set(false);
            return;
        }

        if (this.localStorageService.getItem('admin_AuthenticatedUser')) {
            this.resetLocalStorage();
        }

        await this.authService
            .setControlledUid(controlledUid.replace(/\s/g, ''))
            .then(async () => {
                this.dashboardOwnerUser = this.helperService.getDashboardOwnerUser();
                if (this.helperService.compareUserToLocalStorage('controlled_user')) {
                    this.adminService.controlActive.set(true);
                    this.adminService.controlledUid.set(this.dashboardOwnerUser.uid);
                    window.location.reload();
                } else {
                    this.toastService.info(this.translate.instant('navbar.customer_not_found'));
                    this.adminService.loadingControlledUid.set(false);
                    this.adminService.controlActive.set(false);
                }
            })
            .catch(() => {
                this.toastService.info(this.translate.instant('navbar.customer_not_found'));
                this.adminService.loadingControlledUid.set(false);
            });
        console.log('Sets accessmap from localstorage');
        this.featureService.setAccessMapFromLocalstorage();
        this.sidebarService.enableAdminSidebar();
    }
}
